import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Stack,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Button,
  Title,
  Text,
  Divider,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import {
  ServiceAdjustmentForm,
  ServiceAdjustmentFormQuestion,
  ServiceAdjustmentFormOption,
} from "../../types/serviceAdjustmentForms";
import DraggableQuestion from "./DraggableQuestion";

interface AdjustmentFormConfigStepProps {
  formData: ServiceAdjustmentForm;
  onChange: (field: string, value: any) => void;
  onQuestionsChange: (questions: ServiceAdjustmentFormQuestion[]) => void;
  errors: Record<string, string>;
}

function AdjustmentFormConfigStep({
  formData,
  onChange,
  onQuestionsChange,
  errors,
}: AdjustmentFormConfigStepProps) {
  const handleAddQuestion = () => {
    const newQuestion: ServiceAdjustmentFormQuestion = {
      question_id: `temp-q-${Date.now()}`, // Temporary stable ID.  All temp IDs get dropped before API call.
      question: "",
      priority: formData.questions.length + 1,
      options: [],
    };
    onQuestionsChange([...formData.questions, newQuestion]);
  };

  const handleQuestionChange = (index: number, value: string) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question: value,
    };
    onQuestionsChange(updatedQuestions);
  };

  const handleDeleteQuestion = (index: number) => {
    const updatedQuestions = formData.questions.filter((_, i) => i !== index);
    // Recalculate priorities
    const reorderedQuestions = updatedQuestions.map((q, idx) => ({
      ...q,
      priority: idx + 1,
    }));
    onQuestionsChange(reorderedQuestions);
  };

  const handleAddOption = (questionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    const newOption: ServiceAdjustmentFormOption = {
      option_id: `temp-o-${Date.now()}-${Math.random()}`, // Temporary stable ID
      answer: "",
      duration_delta_mins: null,
      price_delta: null,
      priority: updatedQuestions[questionIndex].options.length + 1,
    };
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      options: [...updatedQuestions[questionIndex].options, newOption],
    };
    onQuestionsChange(updatedQuestions);
  };

  const handleOptionChange = (
    questionIndex: number,
    optionIndex: number,
    field: string,
    value: any,
  ) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      [field]: value,
    };
    onQuestionsChange(updatedQuestions);
  };

  const handleDeleteOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options = updatedQuestions[
      questionIndex
    ].options.filter((_, i) => i !== optionIndex);
    // Recalculate priorities
    updatedQuestions[questionIndex].options = updatedQuestions[
      questionIndex
    ].options.map((opt, idx) => ({
      ...opt,
      priority: idx + 1,
    }));
    onQuestionsChange(updatedQuestions);
  };

  const handleReorderOptions = (
    questionIndex: number,
    oldIndex: number,
    newIndex: number,
  ) => {
    const updatedQuestions = [...formData.questions];
    const reorderedOptions = arrayMove(
      updatedQuestions[questionIndex].options,
      oldIndex,
      newIndex,
    );
    updatedQuestions[questionIndex].options = reorderedOptions.map(
      (opt, idx) => ({
        ...opt,
        priority: idx + 1,
      }),
    );
    onQuestionsChange(updatedQuestions);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find indices by question_id with stable index-based fallback
    const oldIndex = formData.questions.findIndex(
      (q, idx) => (q.question_id || `temp-q-${idx}`) === activeId,
    );
    const newIndex = formData.questions.findIndex(
      (q, idx) => (q.question_id || `temp-q-${idx}`) === overId,
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedQuestions = arrayMove(
        formData.questions,
        oldIndex,
        newIndex,
      );
      const updatedQuestions = reorderedQuestions.map((q, idx) => ({
        ...q,
        priority: idx + 1,
      }));
      onQuestionsChange(updatedQuestions);
    }
  };

  return (
    <Stack gap="md">
      <Title order={4}>Form Configuration</Title>

      <TextInput
        label="Form Name"
        placeholder="Enter form name"
        value={formData.name}
        onChange={(e) => onChange("name", e.target.value)}
        maxLength={128}
        required
        error={errors.name}
      />

      <Textarea
        label="Description"
        placeholder="Enter form description (optional)"
        value={formData.description || ""}
        onChange={(e) => onChange("description", e.target.value)}
        maxLength={500}
        minRows={3}
        error={errors.description}
      />

      <NumberInput
        label="Priority"
        placeholder="Enter priority number"
        value={formData.priority}
        onChange={(value) => onChange("priority", value)}
        min={1}
        required
        error={errors.priority}
      />

      <Switch
        label="Is Active"
        checked={formData.is_active}
        onChange={(event) => onChange("is_active", event.currentTarget.checked)}
      />

      <Divider my="md" />

      <Stack gap="xs">
        <Title order={5}>Questions & Answers</Title>
        <Text size="sm" c="dimmed">
          Drag and drop to reorder questions and answer options
        </Text>
      </Stack>

      {errors.questions && (
        <Text size="sm" c="red">
          {errors.questions}
        </Text>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={formData.questions.map(
            (q, idx) => q.question_id || `temp-q-${idx}`,
          )}
          strategy={verticalListSortingStrategy}
        >
          <Stack gap="md">
            {formData.questions.map((question, index) => (
              <DraggableQuestion
                key={question.question_id || `question-${index}`}
                question={question}
                index={index}
                onQuestionChange={handleQuestionChange}
                onDeleteQuestion={handleDeleteQuestion}
                onAddOption={handleAddOption}
                onOptionChange={handleOptionChange}
                onDeleteOption={handleDeleteOption}
                onReorderOptions={handleReorderOptions}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      <Button
        variant="light"
        leftSection={<IconPlus size={16} />}
        onClick={handleAddQuestion}
      >
        Add Question
      </Button>
    </Stack>
  );
}

export default AdjustmentFormConfigStep;
